export interface CarAffordabilityInputs {
  purchaseAmount: number;
  deposit: number;
  interestRate: number; // Annual percentage rate
  repaymentTerm: number; // in months
  balloonPayment: number;
  monthlyIncome: number; // Net monthly income
}

export interface CarAffordabilityResult {
  loanAmount: number; // Purchase amount - deposit
  monthlyRepayment: number;
  affordabilityRatio: number; // Percentage of income spent on car payment
  affordabilityStatus: "green" | "amber" | "red";
  affordabilityMessage: string;
  totalAmountToRepay: number;
  totalInterest: number;
}

/**
 * Calculate monthly car loan payment with balloon payment
 * Uses standard amortization formula with residual/balloon payment
 */
export function calculateCarMonthlyPayment(
  principal: number,
  annualInterestRate: number,
  termMonths: number,
  balloonPayment: number
): number {
  if (principal <= 0 || termMonths <= 0) return 0;

  const monthlyRate = annualInterestRate / 100 / 12;

  if (monthlyRate === 0) {
    // If interest rate is 0, calculate: (principal - balloon) / months
    return (principal - balloonPayment) / termMonths;
  }

  // Formula for loan with balloon payment:
  // P = [PV - FV / (1 + r)^n] / [(1 - (1 + r)^-n) / r]
  // Where:
  // P = monthly payment
  // PV = principal (loan amount)
  // FV = balloon payment (future value)
  // r = monthly rate
  // n = number of periods (months)

  const principalLessBalloon =
    principal - balloonPayment / Math.pow(1 + monthlyRate, termMonths);
  const annuityFactor =
    (1 - Math.pow(1 + monthlyRate, -termMonths)) / monthlyRate;

  return principalLessBalloon / annuityFactor;
}

/**
 * Calculate car affordability
 */
export function calculateCarAffordability(
  inputs: CarAffordabilityInputs
): CarAffordabilityResult {
  const loanAmount = inputs.purchaseAmount - inputs.deposit;

  if (loanAmount <= 0 || inputs.repaymentTerm <= 0) {
    return {
      loanAmount: 0,
      monthlyRepayment: 0,
      affordabilityRatio: 0,
      affordabilityStatus: "red",
      affordabilityMessage: "Please enter valid values",
      totalAmountToRepay: 0,
      totalInterest: 0,
    };
  }

  const monthlyRepayment = calculateCarMonthlyPayment(
    loanAmount,
    inputs.interestRate,
    inputs.repaymentTerm,
    inputs.balloonPayment
  );

  // Calculate affordability only if monthly income is provided
  let affordabilityRatio = 0;
  let affordabilityStatus: "green" | "amber" | "red" = "red";
  let affordabilityMessage = "Enter monthly income to calculate affordability";

  if (inputs.monthlyIncome > 0) {
    affordabilityRatio = (monthlyRepayment / inputs.monthlyIncome) * 100;

    if (affordabilityRatio < 30) {
      affordabilityStatus = "green";
      affordabilityMessage = "Excellent - Very affordable";
    } else if (affordabilityRatio >= 30 && affordabilityRatio <= 60) {
      affordabilityStatus = "amber";
      affordabilityMessage = "Moderate - Consider your budget";
    } else {
      affordabilityStatus = "red";
      affordabilityMessage = "High risk - May be unaffordable";
    }
  }

  // Calculate total interest (approximation)
  const totalAmountToRepay =
    monthlyRepayment * inputs.repaymentTerm + inputs.balloonPayment;
  const totalInterest = totalAmountToRepay - loanAmount;

  return {
    loanAmount,
    monthlyRepayment,
    affordabilityRatio,
    affordabilityStatus,
    affordabilityMessage,
    totalAmountToRepay,
    totalInterest,
  };
}

export interface HomeAffordabilityInputs {
  grossMonthlyIncome: number;
  deposit: number;
  interestRate: number; // Annual percentage rate
  repaymentTermYears: number; // up to 30 years
  maxDebtToIncomeRatio: number; // Typically 30%
}

export interface HomeAffordabilityResult {
  maxAffordableHomePrice: number; // Loan amount + deposit
  maxLoanAmount: number; // Maximum loan they can qualify for
  monthlyRepayment: number;
  totalInterest: number;
  totalLoanRepayment: number;
  affordabilityRatio: number; // Percentage of income spent on repayment
  affordabilityStatus: "green" | "amber" | "red";
  affordabilityMessage: string;
}

/**
 * Calculate maximum loan amount based on income percentage
 * Typically 30% of gross monthly income can go towards home repayment
 */
export function calculateMaximumLoanAmount(
  grossMonthlyIncome: number,
  maxDebtToIncomeRatio: number, // as percentage (e.g., 30 for 30%)
  annualInterestRate: number,
  termYears: number
): number {
  if (grossMonthlyIncome <= 0 || maxDebtToIncomeRatio <= 0 || termYears <= 0) {
    return 0;
  }

  // Maximum monthly payment they can afford
  const maxMonthlyPayment = (grossMonthlyIncome * maxDebtToIncomeRatio) / 100;

  // Now we need to reverse calculate the loan amount from the monthly payment
  const monthlyRate = annualInterestRate / 100 / 12;
  const numberOfPayments = termYears * 12;

  if (monthlyRate === 0) {
    // If interest rate is 0, loan amount is simply monthly payment * number of payments
    return maxMonthlyPayment * numberOfPayments;
  }

  // Reverse of standard loan formula:
  // P = [M * (1 - (1 + r)^-n)] / r
  // Where P is principal, M is monthly payment
  const principal =
    (maxMonthlyPayment * (1 - Math.pow(1 + monthlyRate, -numberOfPayments))) /
    monthlyRate;

  return principal;
}

/**
 * Calculate home affordability
 */
export function calculateHomeAffordability(
  inputs: HomeAffordabilityInputs
): HomeAffordabilityResult {
  // Calculate maximum loan amount
  const maxLoanAmount = calculateMaximumLoanAmount(
    inputs.grossMonthlyIncome,
    inputs.maxDebtToIncomeRatio,
    inputs.interestRate,
    inputs.repaymentTermYears
  );

  // Total affordable home price
  const maxAffordableHomePrice = maxLoanAmount + inputs.deposit;

  // Calculate actual monthly payment based on max loan
  const monthlyRepayment =
    inputs.grossMonthlyIncome * (inputs.maxDebtToIncomeRatio / 100);

  // Calculate total interest (approximation)
  const totalLoanRepayment = monthlyRepayment * inputs.repaymentTermYears * 12;
  const totalInterest = totalLoanRepayment - maxLoanAmount;

  // Affordability ratio is just the debt-to-income ratio
  const affordabilityRatio = inputs.maxDebtToIncomeRatio;

  let affordabilityStatus: "green" | "amber" | "red";
  let affordabilityMessage: string;

  if (affordabilityRatio <= 25) {
    affordabilityStatus = "green";
    affordabilityMessage = "Excellent - Very affordable";
  } else if (affordabilityRatio > 25 && affordabilityRatio <= 35) {
    affordabilityStatus = "amber";
    affordabilityMessage = "Moderate - Manageable budget";
  } else {
    affordabilityStatus = "red";
    affordabilityMessage = "High risk - May be challenging";
  }

  return {
    maxAffordableHomePrice,
    maxLoanAmount,
    monthlyRepayment,
    totalInterest,
    totalLoanRepayment,
    affordabilityRatio,
    affordabilityStatus,
    affordabilityMessage,
  };
}
